import '@mdi/font/css/materialdesignicons.css';

import { createApp, defineAsyncComponent, nextTick } from 'vue';
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n';
import 'vuetify/styles';
import '@/assets/scss/global.scss';
import '@/assets/css/vuetifyReset.css';
import '@/assets/css/vuetifyLegacyGrid.css';
import '@/assets/css/hostShield.css';
import { createVuetify } from 'vuetify';
import '@fortawesome/fontawesome-free/css/all.css';
import { library, config } from '@fortawesome/fontawesome-svg-core';
config.autoAddCss = false;
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircle, faStar } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowCircleDown,
  faCaretRight,
  faCaretLeft,
  faCaretDown,
  faCircleCheck,
  faCheck,
  faClock,
  faCircleChevronLeft,
  faCircleChevronRight,
  faCircleChevronDown,
  faChevronRight,
  faChevronLeft,
  faExclamationCircle,
  faInfoCircle,
  faTriangleExclamation,
  faTimesCircle,
  faRotateRight,
  faSearch,
  faStar as faStarSolid,
  faTasks,
  faTimes,
  faPrint,
  faFilePdf,
  faUpRightFromSquare,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';

import { i18n, useI18n } from './i18n';
import App from './App.vue';
import { type Configuration } from './ClientConfiguration';

const HOST_MOUNT_ATTRIBUTE = 'data-hes-host-mount';

declare global {
  interface Window {
    dummyHesAssessmentClientConfiguration: Configuration;
    startHesAssessmentClient: (unvalidatedconfiguration: Configuration) => void;
  }
}

export const main = (unvalidatedconfiguration: Configuration): void => {
  const vuetify = createVuetify({
    theme: false,
    // Vuetify 3's breakpoints, matching $grid-breakpoints in src/assets/scss/vuetifySettings.scss.
    display: {
      /* eslint-disable id-length -- Vuetify's breakpoint names */
      thresholds: {
        md: 960,
        lg: 1280,
        xl: 1920,
        xxl: 2560,
      },
      /* eslint-enable id-length */
    },
    locale: {
      adapter: createVueI18nAdapter({ i18n, useI18n }),
      rtl: {
        default: false,
        'ar-SA': true,
        'ar-EG': true,
      },
    },
  });

  library.add(
    faArrowCircleDown,
    faCaretRight,
    faCaretLeft,
    faCaretDown,
    faCircle,
    faCircleCheck,
    faCheck,
    faClock,
    faCircleChevronLeft,
    faCircleChevronRight,
    faCircleChevronDown,
    faChevronRight,
    faChevronLeft,
    faExclamationCircle,
    faInfoCircle,
    faTriangleExclamation,
    faTimesCircle,
    faRotateRight,
    faSearch,
    faStar,
    faStarSolid,
    faTasks,
    faTimes,
    faPrint,
    faFilePdf,
    faUpRightFromSquare,
    faUser,
    faUsers,
  );

  try {
    unvalidatedconfiguration.applicationDivId = unvalidatedconfiguration.applicationDivId
      ? unvalidatedconfiguration.applicationDivId
      : 'app';

    const vueApp = document.getElementById(unvalidatedconfiguration.applicationDivId);

    if (vueApp) {
      const hesVueAppWrapper = document.createElement('div');

      const hostMount = vueApp.cloneNode(true) as HTMLElement;

      hesVueAppWrapper.classList.add('hesAPISPA');
      // hostShield.css exempts the element carrying this attribute: the host owns its mount point.
      hostMount.setAttribute(HOST_MOUNT_ATTRIBUTE, '');
      hesVueAppWrapper.appendChild(hostMount);
      vueApp.replaceWith(hesVueAppWrapper);
    }

    if (!unvalidatedconfiguration.clientBaseUrl) {
      let baseUrl = '';
      const scripts = document.getElementsByTagName('script');

      for (let scriptIdx = 0; scriptIdx < scripts.length; scriptIdx += 1) {
        if (scripts[scriptIdx].src.endsWith('assessment-client.es.js')) {
          baseUrl = scripts[scriptIdx].src.replace('/assessment-client.es.js', '');
        }
      }

      if (!baseUrl) {
        throw new Error('client js url cannot be found');
      }

      unvalidatedconfiguration.clientBaseUrl = baseUrl;
    }

    const { head } = document;

    if (!['localvue', 'ci'].includes(unvalidatedconfiguration.apiEnvironment)) {
      const css = document.createElement('link');

      css.setAttribute('rel', 'stylesheet');
      css.setAttribute('type', 'text/css');
      css.setAttribute('media', 'all');
      css.setAttribute('href', `${unvalidatedconfiguration.clientBaseUrl}/assets/assessment-client.css`);
      head.appendChild(css);
    }

    const app = createApp(App, {
      unvalidatedconfiguration,
    })
      .component('font-awesome-icon', FontAwesomeIcon)
      .component(
        'Activity',
        defineAsyncComponent(() => import('./components/reportComponents/Activity.vue')),
      )
      .component(
        'ActivityContainer',
        defineAsyncComponent(() => import('./components/reportComponents/ActivityContainer.vue')),
      )
      .component(
        'Badges',
        defineAsyncComponent(() => import('./components/reportComponents/Badges.vue')),
      )
      .component(
        'CareerList',
        defineAsyncComponent(() => import('./components/reportComponents/CareerList.vue')),
      )
      .component(
        'CombiningIntelligences',
        defineAsyncComponent(() => import('./components/reportComponents/CombiningIntelligences.vue')),
      )
      .component(
        'EmotionalIntelligencesTraits',
        defineAsyncComponent(() => import('./components/reportComponents/EmotionalIntelligencesTraits.vue')),
      )
      .component(
        'EmotionalIntelligencesResultsText',
        defineAsyncComponent(() => import('./components/reportComponents/EmotionalIntelligencesResultsText.vue')),
      )
      .component(
        'Error',
        defineAsyncComponent(() => import('./components/Error.vue')),
      )
      .component(
        'GraphSection',
        defineAsyncComponent(() => import('./components/reportComponents/GraphSection.vue')),
      )
      .component(
        'GraphSectionGraph',
        defineAsyncComponent(() => import('./components/reportComponents/GraphSectionGraph.vue')),
      )
      .component(
        'GraphGroupSection',
        defineAsyncComponent(() => import('./components/reportComponents/GraphGroupSection.vue')),
      )
      .component(
        'GraphSubGroupingSection',
        defineAsyncComponent(() => import('./components/reportComponents/GraphSubGroupingSection.vue')),
      )
      .component(
        'Header',
        defineAsyncComponent(() => import('./components/Header.vue')),
      )
      .component(
        'IntelligencesGraphSection',
        defineAsyncComponent(() => import('./components/reportComponents/IntelligencesGraphSection.vue')),
      )
      .component(
        'IntelligencesGraphSectionGraph',
        defineAsyncComponent(() => import('./components/reportComponents/IntelligencesGraphSectionGraph.vue')),
      )
      .component(
        'IntelligencesTopCareers',
        defineAsyncComponent(() => import('./components/reportComponents/IntelligencesTopCareers.vue')),
      )
      .component(
        'TruTalentsReport',
        defineAsyncComponent(() => import('./components/TruTalentsReport.vue')),
      )
      .component(
        'TruTalentsReport2',
        defineAsyncComponent(() => import('./components/TruTalentsReport2.vue')),
      )
      .component(
        'Rating',
        defineAsyncComponent(() => import('./components/reportComponents/Rating.vue')),
      )
      .component(
        'Report',
        defineAsyncComponent(() => import('./components/Report.vue')),
      )
      .component(
        'Section',
        defineAsyncComponent(() => import('./components/reportComponents/Section.vue')),
      )
      .component(
        'SkillsGraphSection',
        defineAsyncComponent(() => import('./components/reportComponents/SkillsGraphSection.vue')),
      )
      .component(
        'SkillsGraphSectionGraph',
        defineAsyncComponent(() => import('./components/reportComponents/SkillsGraphSectionGraph.vue')),
      )
      .component(
        'StarRating',
        defineAsyncComponent(() => import('./components/reportComponents/StarRating.vue')),
      )
      .component(
        'SubSection',
        defineAsyncComponent(() => import('./components/reportComponents/SubSection.vue')),
      )
      .component(
        'SummaryReportIntelligences',
        defineAsyncComponent(() => import('./components/reportComponents/SummaryReportIntelligences.vue')),
      )
      .component(
        'SummaryReportLearningProductivity',
        defineAsyncComponent(() => import('./components/reportComponents/SummaryReportLearningProductivity.vue')),
      )
      .component(
        'SummaryReportPersonality',
        defineAsyncComponent(() => import('./components/reportComponents/SummaryReportPersonality.vue')),
      )
      .component(
        'SummaryReportSkills',
        defineAsyncComponent(() => import('./components/reportComponents/SummaryReportSkills.vue')),
      )
      .component(
        'TablePagination',
        defineAsyncComponent(() => import('./components/reportComponents/TablePagination.vue')),
      )
      .component(
        'TextBlock',
        defineAsyncComponent(() => import('./components/reportComponents/TextBlock.vue')),
      )
      .component(
        'TextBlockActivityWrapper',
        defineAsyncComponent(() => import('./components/reportComponents/TextBlockActivityWrapper.vue')),
      );

    app.use(i18n);
    app.use(vuetify);
    app.config.performance = true;
    app.config.performance = import.meta.env.VUE_APP_ENABLE_PERFORMANCE === 'true';

    app.directive('focus', {
      mounted(element, binding) {
        if (binding.value) {
          element.focus();
        }
      },
      updated(element, binding) {
        if (binding.value) {
          nextTick(() => {
            element.focus();
          });
        }
      },
    });

    app.mount(`#${unvalidatedconfiguration.applicationDivId}`);
  } catch (error) {
    if (error instanceof Error) {
      document.write(`<pre>${error.message}</pre>`);
    }

    throw error;
  }
};

// this is left here to ensure vite crawls the js code.  Otherwise it does not ouput any.
if (window.dummyHesAssessmentClientConfiguration) {
  const unvalidatedconfiguration = window.dummyHesAssessmentClientConfiguration || ({} as Configuration);

  main(unvalidatedconfiguration);
}

window.startHesAssessmentClient = main;
